// LT-511: meeting-info column. Host avatar (or initials), eyebrow with
// host name, title, duration / location / timezone rows, optional
// description blurb.
import React from 'react';
import { Clock, Globe, Video } from 'lucide-react';
import { AvailabilityResponse } from '../../../services/publicBookingService';
import { initialsOf } from '../formatters';

interface Props {
  data: AvailabilityResponse;
}

const MeetingInfoPanel: React.FC<Props> = ({ data }) => (
  <div className="flex flex-col gap-5">
    {data.owner_avatar_url ? (
      <img
        src={data.owner_avatar_url}
        alt={data.owner_name ?? 'Host'}
        className="w-12 h-12 rounded-full object-cover shadow-ds-glow-accent"
      />
    ) : (
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center font-semibold text-base text-white shadow-ds-glow-accent"
        style={{ background: 'linear-gradient(135deg, var(--accent), var(--surface-3))' }}
        aria-hidden="true"
      >
        {initialsOf(data.owner_name)}
      </div>
    )}
    <div>
      {data.owner_name && (
        <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400 mb-2">
          {data.owner_name}
        </div>
      )}
      <h1 className="m-0 text-2xl font-semibold leading-tight text-slate-50">
        {data.title}
      </h1>
    </div>

    <div className="flex flex-col gap-2.5 text-[13px] text-slate-300">
      <div className="flex items-center gap-2.5">
        <Clock className="w-3.5 h-3.5 text-slate-400" />
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{data.duration_minutes} min</span>
      </div>
      {data.location_label && (
        <div className="flex items-center gap-2.5">
          <Video className="w-3.5 h-3.5 text-slate-400" />
          <span>{data.location_label}</span>
        </div>
      )}
      <div className="flex items-center gap-2.5">
        <Globe className="w-3.5 h-3.5 text-slate-400" />
        <span className="font-mono text-xs">{data.timezone}</span>
      </div>
    </div>

    {data.description && (
      <p className="m-0 text-[13px] leading-relaxed text-slate-400">
        {data.description}
      </p>
    )}
  </div>
);

export default MeetingInfoPanel;
